export const LOAD_TRADE_NOTES = 'trade-notes/LOAD_TRADE_NOTES'
export const ADD_TRADE_NOTE = 'trade-notes/ADD_TRADE_NOTE'
export const LOAD_UNREAD_COUNTS = 'trade-notes/LOAD_UNREAD_COUNTS'
export const APPLY_READ_PATCHES = 'trade-notes/APPLY_READ_PATCHES'

/**
 * One note's read receipt as "Mark all" returns it (LOS-7093): the fields patched onto the
 * note already in the store. The Signing Portal's bulk response has the same shape.
 */
export type TradeNoteReadPatch = {
  noteId: string
  readAt: string
  readByAuth0Id: string | null
  readByName: string | null
}

/**
 * Unread counts for badge surfaces (read-state design): the trade-list Stream column badges by
 * master trade; the tab and sub-trade views by sub-trade. Unread on this side = a
 * portal-authored note with no read receipt, computed by the backend from receipt absence.
 */
export interface UnreadCounts {
  byMasterTrade: Record<string, number>
  bySubTrade: Record<string, number>
}

/**
 * The Activity Stream is not a notes list (ADR_LOS-028 D9). The designs interleave
 * user-authored notes with system events — Document Upload, Trade Edits, Status Update —
 * each badged with its kind and its source. Phase 1 renders ONLY `note` items and derives no
 * events; the discriminator is here so events can join without reshaping this duck or the
 * rendering component.
 */
export type ActivityItemKind = 'note' | 'event'

export type ActivityItemSource = 'LendOS' | 'SigningPortal'

/** Author's relationship to the sub-trade, derived by the backend at read time. */
export type TradeNoteAuthorRole = 'agent' | 'buyer' | 'seller' | 'unknown'

export interface ActivityStreamItem {
  kind: ActivityItemKind
  id: string
  source: ActivityItemSource
  createdAt: string
}

export interface TradeNoteItem extends ActivityStreamItem {
  kind: 'note'
  subTradeId: string
  masterTradeId: string
  authorName: string
  authorParty: string
  authorRole: TradeNoteAuthorRole
  body: string
  /** Reserved for threading; always null in Phase 1. */
  parentNoteId: string | null
  /** The author's Auth0 user id — provenance; not rendered in Phase 1. */
  auth0Id: string | null
  /** "Directed to" — the note's recipients. Phase 1 always sends to every sub-trade party. */
  recipients: string[]
  /**
   * Read state (read-state design): the FIRST reader, identified by the STABLE auth0 id —
   * deliberately no display name (names drift, receipts must not go stale). The id is optional
   * on the ledger, so `readAt` is the unread discriminator: "New" = readAt === null &&
   * source === 'SigningPortal' (a note is only New on the surface opposite its origin).
   * The UI calls a receipted note "actioned" (LOS-6987).
   */
  readByAuth0Id: string | null
  readAt: string | null
  /**
   * The reader's name for the "Actioned by" line, resolved by the backend at read time (never
   * stored). Null while unread, and when the reader has no name or email on record.
   */
  readByName: string | null
  /**
   * The note's most recent "Mark as actioned" undo (LOS-6988), for the footer's
   * "Reopened by {name} · {date}" line. Set only while the note is unactioned again — a receipt
   * is always newer. The name is resolved by the backend at read time, null when it cannot be.
   */
  reopenedByAuth0Id: string | null
  reopenedAt: string | null
  reopenedByName: string | null
}

export type ActivityItem = TradeNoteItem

/**
 * Chronological by creation time, oldest first — the acceptance criterion. Ties break on id so
 * the order cannot shuffle between refreshes when two notes share a timestamp (plausible: the
 * two products write independently).
 */
export const sortActivityItems = <T extends ActivityStreamItem>(
  items: T[],
): T[] =>
  [...items].sort((a, b) => {
    const byTime = a.createdAt.localeCompare(b.createdAt)
    return byTime !== 0 ? byTime : a.id.localeCompare(b.id)
  })
